import { useMemo } from 'react';
import { differenceInDays } from 'date-fns';
import { DateRangeValue } from 'src/components/shared/DateRangeField';
import { SessionAggregation } from 'src/@types';

export function useUTCDateRangeValue({ startDate, endDate }: Required<DateRangeValue>) {
  return useMemo(() => {
    /**
     * Extract date components from local timezone to preserve the selected date.
     * This prevents timezone conversion issues where dates can shift backward by one day
     * in timezones behind UTC (e.g., PST UTC-8).
     *
     * Uses getFullYear(), getMonth(), and getDate() which return values in the local timezone,
     * then constructs UTC date strings directly from those components.
     */
    const getUTCDateString = (date: Date, isEndOfDay: boolean) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const time = isEndOfDay ? '23:59:59.999' : '00:00:00.000';
      return `${year}-${month}-${day}T${time}Z`;
    };

    return {
      startDate: new Date(getUTCDateString(startDate, false)),
      endDate: new Date(getUTCDateString(endDate, true)),
    };
  }, [startDate, endDate]);
}

export function useUtilizationBarChartAggregateBy({ startDate, endDate }: Required<DateRangeValue>) {
  return useMemo(() => {
    const diffDays = differenceInDays(endDate, startDate) + 1;
    if (diffDays < 2) {
      return SessionAggregation.Hour;
    }
    if (diffDays < 32) {
      return SessionAggregation.Day;
    }
    return SessionAggregation.Month;
  }, [startDate, endDate]);
}
